import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Title, Meta } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Data,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { ImageCachePipe } from '../pipes/ImageCachePipe';
import { platePath, toCanonicalUrl } from '../utils/canonical-url';
import { HOME_DESCRIPTION, PAGE_META } from '../page-meta';
import { seriesMeta } from '../utils/series-url';
import { site } from '../site-config';

declare global {
  interface Window {
    gtag: any;
  }
}

@Injectable({ providedIn: 'root' })
export class RouteDataService {
  constructor(
    private router: Router,
    private titleService: Title,
    private metaService: Meta,
    private cachePipe: ImageCachePipe,
    @Inject(DOCUMENT) private document: Document
  ) { }

  get(): void {
    const data = this.getRouteSnapshot();
    var routeData = { data: data.data, params: data.params };
    var titleToSet = PAGE_META['/'].title; // base
    var description = HOME_DESCRIPTION;
    var image = this.cachePipe.transform('https://cdn.platex.co.uk/banners/PLATEX_R8.jpg');
    var shouldIndex = true;
    if (routeData.data['title'] != undefined)
      titleToSet = this.constructString(routeData.data['title'], routeData);

    if (routeData.data['image'] != undefined)
      image = this.constructString(this.cachePipe.transform(routeData.data['image']), routeData);

    // Registration-series pages carry their own title/description per series.
    if (routeData.data['series'] && routeData.params['releaseYear']) {
      const meta = seriesMeta(routeData.params['releaseYear']);
      titleToSet = meta.title;
      description = meta.description;
    }

    if (routeData.data['index'] === false) {
      shouldIndex = false;
    }

    if (routeData.data['description'] != undefined)
      description = this.constructString(
        routeData.data['description'],
        routeData
      );

    this.setMeta(titleToSet, description, image, shouldIndex);
  }

  /** sameUrl: a page refining the meta of the URL it was loaded on (e.g. a blog
   *  post once its data arrives) keeps that URL's static structured data. */
  public setMeta(title: string, description: string, image: string, shouldIndex: boolean, sameUrl = false): void {
    window.gtag('event', 'page_view', {
      page_title: title,
    });
    this.titleService.setTitle(title);
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'image', content: image });
    this.metaService.updateTag({ name: 'og:image', content: image });
    this.metaService.updateTag({
      name: 'og:description',
      content: description,
    });

    // Self-referencing canonical + og:url + og:title — emitted on EVERY route
    // (including noindex routes: a noindexed page still carries a self-canonical).
    // The static index.html canonical block and static og:url/og:title were
    // removed; this is now the sole owner of these per-route tags.
    const canonicalUrl = this.buildCanonicalUrl();
    this.setCanonical(canonicalUrl);
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });
    this.metaService.updateTag({ property: 'og:title', content: title });

    // Only production is ever indexable. Non-indexable production pages keep
    // `follow` (SEO spec); every other host is noindex, nofollow. The CDN edge
    // sends the same decision as an X-Robots-Tag header in the first response.
    if (site.name !== 'production') {
      this.metaService.updateTag({ name: 'robots', content: 'noindex, nofollow' });
    } else if (shouldIndex) {
      this.metaService.removeTag("name='robots'");
    } else {
      this.metaService.updateTag({ name: 'robots', content: 'noindex, follow' });
    }

    // Structured data baked into a page's static HTML (build-time head) only
    // describes the URL it was served for; drop it once the visitor navigates.
    if (!sameUrl && this.metaCalls++ > 0) {
      this.document.head
        .querySelectorAll('script[type="application/ld+json"][data-static-head]')
        .forEach((el) => el.remove());
    }
  }

  private metaCalls = 0;

  // Absolute canonical URL for the current route. Every individual plate —
  // whether reached via the canonical /plate/{REG} route or the legacy
  // /plate-detail/{REG} alias — collapses to /plate/{REGISTRATION} (uppercased,
  // space-free, term segment + query dropped) via the same shared helper used
  // to build the crawlable card anchor, so the two strings are byte-for-byte
  // identical. The regex matches /plate and /plate-detail but NOT /plate-viewer,
  // /plate-detail-legacy or /plate-detail-v2 (those keep their own self-canonical).
  private buildCanonicalUrl(): string {
    const snapshot = this.getRouteSnapshot();
    const reg = snapshot.params['registration'];
    const path = this.router.url.split('?')[0].split('#')[0];
    if (reg != null && /^\/plates?(-detail)?\//.test(path)) {
      return toCanonicalUrl(platePath(reg));
    }
    // Per-route canonical override for legacy taxonomy aliases. A route can
    // declare data.canonical as a site-relative template (e.g.
    // '/plates/initials/{initials}') so the old URL renders in place but points
    // its canonical at the new /plates/... destination — no 301 redirect.
    // Params are substituted verbatim in LOWERCASE: taxonomy URLs are lowercase,
    // unlike plate regs (which constructString uppercases for titles).
    const override = snapshot.data['canonical'];
    if (typeof override === 'string' && override.length > 0) {
      return toCanonicalUrl(this.fillCanonicalTemplate(override, snapshot.params));
    }
    return toCanonicalUrl(path);
  }

  // Substitute {param} placeholders in a canonical template with the matching
  // route param, lowercased. Missing params collapse to an empty segment.
  private fillCanonicalTemplate(template: string, params: Params): string {
    return template.replace(/\{([^}]+)\}/g, (_match: string, key: string) => {
      const value = params[key];
      return value != null ? String(value).toLowerCase() : '';
    });
  }

  // Ensure exactly one <link rel="canonical"> in <head>, updating its href.
  // Angular's Meta service only manages <meta>, so the <link> is handled here.
  private setCanonical(url: string): void {
    let link: HTMLLinkElement = this.document.head.querySelector(
      "link[rel='canonical']"
    );
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }

  private constructString(
    text: string,
    routeInfo: { data: Data; params: Params }
  ): string {
    const r = new RegExp('(?<={)(.*?)(?=})');
    const regexResults = r.exec(text);

    var builtText = text;
    if (regexResults != null) {
      regexResults.forEach((key: string) => {
        builtText = builtText.replace(
          `{${key}}`,
          (routeInfo.params[key] as string).toUpperCase()
        );
      });
    }

    return builtText;
  }

  private getRouteSnapshot(): ActivatedRouteSnapshot {
    let route: ActivatedRoute = this.router.routerState.root;
    while (route!.firstChild) {
      route = route.firstChild;
    }
    return route.snapshot;
  }
}
